import { Code, ConnectError } from '@bufbuild/connect';
import { useCallback, useEffect, useState } from 'react';

import { AdminOrganizationListStreamSortBy } from 'protocol/api/core/svc_adminka_pb';
import { Organization } from 'protocol/model/dto_organization_pb';

import api from '@/shared/api/api';

import { ORGANIZATION_LIST_PAGE_SIZE, OrganizationListFilter } from '../config';

type UseOrganizationListQueryParams = {
  enabled?: boolean;
  filter?: OrganizationListFilter;
  limit?: number;
  search?: string;
  sortBy?: AdminOrganizationListStreamSortBy;
};

type UseOrganizationListQueryResult = {
  hasError: boolean;
  hasMore: boolean;
  loading: boolean;
  organizations: Organization[];
  loadMore: () => void;
};

/**
 * Reads `AdminOrganizationListStream` into local state instead of the shared
 * `organizations` slice. Use it for lists that live next to another list on the
 * same screen — the slice is a single global bucket, so two consumers would
 * overwrite each other's data.
 *
 * `filter` is compared by reference: pass a constant from the entity config or
 * a `useMemo`d value, otherwise every render restarts the stream.
 */
export const useOrganizationListQuery = (
  params: UseOrganizationListQueryParams,
): UseOrganizationListQueryResult => {
  const {
    enabled = true,
    filter,
    limit = ORGANIZATION_LIST_PAGE_SIZE,
    search = '',
    sortBy = AdminOrganizationListStreamSortBy.AdminOrganizationListStreamSortByName,
  } = params;

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [startFromOrganizationID, setStartFromOrganizationID] = useState('');

  // A different filter or search term is a different list, so paging starts over.
  useEffect(() => {
    setOrganizations([]);
    setHasMore(false);
    setStartFromOrganizationID('');
  }, [filter, search]);

  useEffect(() => {
    if (!enabled) {
      // A run aborted by disabling the hook has no successor to hand the
      // loading flag over to, so it is cleared here.
      setLoading(false);

      return undefined;
    }

    const abortController = new AbortController();

    const loadOrganizations = async () => {
      setLoading(true);
      setHasError(false);

      try {
        const organizationListStream =
          api.core.admin.adminOrganizationListStream(
            {
              Filter: filter,
              Limit: limit,
              SearchString: search,
              Sort: [{ Ascending: true, By: sortBy }],
              StartFromOrganizationID: startFromOrganizationID,
            },
            { signal: abortController.signal },
          );

        for await (const { Update } of organizationListStream) {
          if (Update.case === 'InitialOrganizations') {
            const page = Update.value.Organizations;

            setOrganizations((current) =>
              startFromOrganizationID ? [...current, ...page] : page,
            );
            setHasMore(page.length === limit);
            setLoading(false);
          }

          if (Update.case === 'OrganizationUpdated') {
            const updated = Update.value;

            setOrganizations((current) =>
              current.map((organization) =>
                organization.ID === updated.ID ? updated : organization,
              ),
            );
          }
        }
      } catch (error) {
        if (error instanceof ConnectError && error.code === Code.Canceled) {
          return;
        }

        setHasError(true);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      abortController.abort();
    };
  }, [enabled, filter, limit, search, sortBy, startFromOrganizationID]);

  const loadMore = useCallback(() => {
    setStartFromOrganizationID(
      organizations[organizations.length - 1]?.ID ?? '',
    );
  }, [organizations]);

  return { hasError, hasMore, loading, organizations, loadMore };
};
